// B3 Clientes: ficha con datos, contacto, vacantes, lo que reporta en su portal, pagos, usuarios del portal
// y el botón «Ver como el cliente» (abre el portal en solo lectura).
import { ArrowLeft, Eye, Mail, Pencil } from "lucide-react";
import { useState } from "react";
import { useGetOne, useNotify, useRefresh, useUpdate } from "ra-core";
import { Link, useParams } from "react-router";
import { Edit } from "@/components/admin/edit";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { dinero, estaBloqueado, fechaCorta, telefonoLegible } from "@/reclutamiento/logica";
import type { Cliente, UsuarioPortal } from "@/reclutamiento/tipos";
import { Encabezado, MarcaSemaforo, Pildora, Seccion } from "../comun";
import { EnlaceAlta, useAltaDe } from "../altas/Alta";
import { BotonMarcarRecibido } from "../pagos";
import {
  ligaVerComo,
  useCandidatos,
  useClientes,
  usePagos,
  useUsuariosPortal,
  useVacantes,
} from "../datos";

const EstadoPago = ({ c }: { c: Cliente }) =>
  !c.pagado_hasta ? (
    <Pildora tono="neutro">Sin candado</Pildora>
  ) : estaBloqueado(c.pagado_hasta) ? (
    <Pildora tono="bad">Pago pendiente · fichas bloqueadas</Pildora>
  ) : (
    <Pildora tono="ok">Pagado hasta {fechaCorta(c.pagado_hasta)}</Pildora>
  );

const VerComo = ({ id, size = "sm" }: { id: string; size?: "sm" | "default" }) => (
  <Button asChild size={size} variant="outline">
    <a href={ligaVerComo(id)} target="_blank" rel="noopener noreferrer">
      <Eye className="size-4" /> Ver como el cliente
    </a>
  </Button>
);

export const ClientesLista = () => {
  const { data: clientes, isPending } = useClientes();
  const { data: vacantes } = useVacantes();
  const { data: candidatos } = useCandidatos();
  return (
    <div>
      <Encabezado
        ceja="B3"
        titulo="Clientes"
        sub="Empresas que ya nos contrataron. Se crean solos al tocar «Convertir en cliente» en un prospecto."
      />
      {isPending ? <p className="text-sm text-muted-foreground">Cargando…</p> : null}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {clientes.map((c) => {
          const vs = vacantes.filter((v) => v.cliente_id === c.id);
          const ks = candidatos.filter((k) => k.cliente_id === c.id);
          return (
            <div key={c.id} className="rounded-xl border bg-card p-4">
              <Link to={`/clientes/${c.id}/show`} className="block">
                <p className="text-lg font-semibold hover:underline">{c.nombre}</p>
                <p className="text-xs text-muted-foreground">{c.giro} · {c.zona}</p>
              </Link>
              <div className="mt-2 flex flex-wrap gap-1"><EstadoPago c={c} />{c.estado === "alta" ? <Pildora tono="revisar">En alta</Pildora> : null}</div>
              <p className="mt-2 text-sm">
                {vs.filter((v) => v.estado === "activa").length} vacantes activa(s){vs.some((v) => v.estado === "borrador") ? " · 1 borrador por confirmar" : ""} · {ks.length} candidatos
              </p>
              <p className="text-xs text-muted-foreground">Contacto: {c.contacto_nombre} ({c.contacto_cargo}) · cliente desde {fechaCorta(c.cliente_desde)}</p>
              <div className="mt-3 flex gap-2">
                <Button asChild size="sm"><Link to={`/clientes/${c.id}/show`}>Abrir ficha</Link></Button>
                <VerComo id={c.id} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Si el cliente nació de un alta automática: el enlace a su proceso en vivo. */
const AltaDelCliente = ({ clienteId }: { clienteId: string }) => {
  const { data: alta } = useAltaDe("cliente_id", clienteId);
  return alta ? <div className="mb-4"><EnlaceAlta alta={alta} /></div> : null;
};

export const ClienteFicha = () => {
  const { id } = useParams();
  const { data: c, isPending, error, refetch } = useGetOne<Cliente & { id: string }>("clientes", { id: id! });
  const { data: vacantes } = useVacantes();
  const { data: candidatos } = useCandidatos();
  const { data: pagos } = usePagos();
  const { data: usuarios } = useUsuariosPortal();
  // El motivo y «Reintentar» los pone el aviso de arriba (ErroresDeConsulta); aquí no se dice «no existe».
  if (error) return <p className="text-sm text-muted-foreground">No se pudo abrir la ficha. <button type="button" className="underline" onClick={() => refetch()}>Reintentar</button></p>;
  if (isPending) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (!c) return <p>No existe ese cliente.</p>;
  const vs = vacantes.filter((v) => v.cliente_id === c.id);
  const ks = candidatos.filter((k) => k.cliente_id === c.id);
  const ps = pagos.filter((p) => p.cliente_id === c.id);
  const us = usuarios.filter((u) => u.cliente_id === c.id);
  const movio = ks.some((k) => k.etapa !== "nuevo");
  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-3 flex items-center justify-between">
        <Button asChild variant="ghost" size="sm"><Link to="/clientes"><ArrowLeft className="size-4" /> Clientes</Link></Button>
        <div className="flex gap-2">
          <VerComo id={c.id} />
          <Button asChild variant="outline" size="sm"><Link to={`/clientes/${c.id}`}><Pencil className="size-4" /> Editar</Link></Button>
        </div>
      </div>
      <Encabezado ceja={c.giro} titulo={c.nombre} sub={<>{c.zona} · cliente desde {fechaCorta(c.cliente_desde)}{c.anuncio_origen ? ` · llegó por el anuncio ${c.anuncio_origen}` : ""}</>} acciones={<EstadoPago c={c} />} />
      <AltaDelCliente clienteId={c.id} />
      <div className="grid gap-4 md:grid-cols-2">
        <Seccion titulo="Datos y contacto">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-muted-foreground">Contacto</dt><dd>{c.contacto_nombre} · {c.contacto_cargo}</dd>
            <dt className="text-muted-foreground">Teléfono</dt><dd className="num">{telefonoLegible(c.telefono)}</dd>
            <dt className="text-muted-foreground">Correo</dt><dd>{c.correo || "—"}</dd>
            <dt className="text-muted-foreground">Quién decide</dt><dd>{c.quien_decide || "—"}</dd>
            {c.deal_id ? (<><dt className="text-muted-foreground">Prospecto</dt><dd><Link className="underline" to={`/deals/${c.deal_id}/show`}>ver cómo llegó</Link></dd></>) : null}
          </dl>
          {c.notas ? <p className="mt-2 text-sm">{c.notas}</p> : null}
        </Seccion>
        <Seccion titulo="Lo que reporta en su portal">
          {movio ? (
            <ul className="space-y-1 text-sm">
              {vs.map((v) => {
                const kv = ks.filter((k) => k.vacante_id === v.id);
                return (
                  <li key={v.id} className="flex justify-between gap-2">
                    <span>{v.puesto}</span>
                    <span className="num">{kv.filter((k) => k.etapa === "contactado").length} contactados · {kv.filter((k) => k.etapa === "entrevistado" || k.etapa === "contratado").length} entrevistados · {kv.filter((k) => k.etapa === "contratado").length} contratados</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Sin reporte: todavía no ha movido a nadie de etapa (no es cero, es «sin reporte»).</p>
          )}
        </Seccion>
        <Seccion titulo="Vacantes" acciones={<Button asChild size="sm" variant="outline"><Link to="/vacantes/create">Alta de vacante</Link></Button>}>
          <ul className="space-y-2 text-sm">
            {vs.map((v) => {
              const kv = ks.filter((k) => k.vacante_id === v.id);
              return (
                <li key={v.id} className="flex items-center justify-between gap-2">
                  <Link className="font-medium underline-offset-2 hover:underline" to={`/vacantes/${v.id}/show`}>{v.puesto}</Link>
                  <span className="flex items-center gap-2">
                    <span className="num inline-flex items-center gap-1"><MarcaSemaforo tipo="cumple" size={13} />{kv.filter((k) => k.veredicto === "cumple").length}</span>
                    <Pildora tono={v.estado === "activa" ? "ok" : v.estado === "borrador" ? "revisar" : "neutro"}>{v.estado}</Pildora>
                  </span>
                </li>
              );
            })}
            {!vs.length ? <li className="text-muted-foreground">Sin vacantes.</li> : null}
          </ul>
        </Seccion>
        <Seccion titulo="Pagos">
          <ul className="space-y-2 text-sm">
            {ps.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-2">
                <span>{p.periodo} · <span className="num">{dinero(p.monto)}</span> · vence {fechaCorta(p.vence)}</span>
                {p.estado === "pagado" ? <Pildora tono="ok">Recibido {fechaCorta(p.pagado_el)}</Pildora> : <BotonMarcarRecibido pago={p} />}
              </li>
            ))}
            {!ps.length ? <li className="text-muted-foreground">Sin periodos.</li> : null}
          </ul>
        </Seccion>
        <Seccion titulo="Usuarios del portal" className="md:col-span-2">
          <ul className="space-y-2 text-sm">
            {us.map((u) => <UsuarioFila key={u.id} u={u} />)}
            {!us.length ? <li className="text-muted-foreground">Sin usuarios.</li> : null}
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">
            V1: la invitación se manda a mano. En Supabase: Authentication → Users → «Invite user» con este correo; al aceptar, el usuario queda ligado a este cliente solo.
          </p>
        </Seccion>
      </div>
    </div>
  );
};

const ETQ_USU: Record<UsuarioPortal["estado"], [string, "ok" | "revisar" | "bad" | "neutro"]> = {
  pendiente_invitacion: ["Falta mandar la invitación", "revisar"],
  invitado: ["Invitado, no ha entrado", "neutro"],
  activo: ["Activo", "ok"],
  desactivado: ["Desactivado", "bad"],
};

const UsuarioFila = ({ u }: { u: UsuarioPortal }) => {
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [txt, tono] = ETQ_USU[u.estado];
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-2">
      <span>
        <b>{u.nombre}</b> · {u.correo || <CorreoFaltante u={u} />}
        <span className="block text-xs text-muted-foreground">Última visita: {u.ultima_visita ? fechaCorta(u.ultima_visita) : "nunca"}</span>
      </span>
      <span className="flex items-center gap-2">
        <Pildora tono={tono}>{txt}</Pildora>
        {u.estado === "pendiente_invitacion" ? (
          <Button
            size="sm"
            variant="outline"
            disabled={isPending || !u.correo}
            onClick={() =>
              update("usuarios_portal", { id: u.id, data: { estado: "invitado", invitado_en: new Date().toISOString() }, previousData: u }, {
                onSuccess: () => { notify("Marcada como enviada", { type: "info" }); refresh(); },
              })
            }
          >
            <Mail className="size-4" /> Ya la mandé
          </Button>
        ) : null}
      </span>
    </li>
  );
};

const CorreoFaltante = ({ u }: { u: UsuarioPortal }) => {
  const [correo, setCorreo] = useState("");
  const [update] = useUpdate();
  const refresh = useRefresh();
  const valido = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(correo.trim());
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <i className="text-[var(--bad-ink)]">sin correo</i>
      <input className="h-8 w-56 rounded-md border bg-background px-2 text-sm" placeholder="correo@empresa.mx" value={correo} onChange={(e) => setCorreo(e.target.value)} aria-label="Correo del usuario del portal" />
      <Button size="sm" variant="outline" disabled={!valido} onClick={() => update("usuarios_portal", { id: u.id, data: { correo: correo.trim().toLowerCase() }, previousData: u }, { onSuccess: refresh })}>Guardar</Button>
    </span>
  );
};

export const ClienteEditar = () => (
  <Edit redirect="show" title="Editar cliente" mutationMode="pessimistic">
    <SimpleForm>
      <TextInput source="nombre" label="Nombre" isRequired />
      <TextInput source="giro" label="Giro" />
      <TextInput source="zona" label="Zona" />
      <TextInput source="contacto_nombre" label="Contacto" />
      <TextInput source="contacto_cargo" label="Cargo" />
      <TextInput source="telefono" label="Teléfono" />
      <TextInput source="correo" label="Correo" />
      <TextInput source="quien_decide" label="Quién decide" />
      <TextInput source="notas" label="Notas" multiline />
    </SimpleForm>
  </Edit>
);

export const useNombreCliente = () => {
  const { data } = useClientes();
  return (id: string | null | undefined) => data.find((c) => c.id === id)?.nombre ?? "—";
};

export default { list: ClientesLista, show: ClienteFicha, edit: ClienteEditar, recordRepresentation: "nombre" };
